import { useEffect, useRef } from 'react';
import React from 'react';

import { Info } from 'lucide-react';
import { useParams } from 'react-router';
import { parseAsString, useQueryState } from 'nuqs';

import { useGetAssignment, useGetSubmitAssignment } from '../../query/assignment';
import { parseEditor } from '../answer-sheet';
import AssignmentStep from '../assignment-step';
import { Card } from '../ui/card';

const MySubmit = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const divRef = useRef<HTMLDivElement>(null);
  const { data: assignment, isLoading: isAssignmentLoading } = useGetAssignment(assignmentId!);
  const { data: assignmentSubmit, isLoading: isSubmitLoading } =
    useGetSubmitAssignment(assignmentId!);

  const [problemId, setProblemId] = useQueryState('problemId', parseAsString.withDefault(''));

  const problem = assignment?.data?.problems.find(problem => problem.id === problemId);
  const problemIndex = assignment?.data?.problems.findIndex(problem => problem.id === problemId);
  const submitProblem = assignmentSubmit?.data?.problems.find(
    problem => problem.snapshotId === problemId,
  );

  useEffect(() => {
    if (!assignment?.data?.problems?.length) return;
    if (problemId) return;
    setProblemId(assignment.data.problems[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment?.data?.problems]);

  useEffect(() => {
    if (!divRef) return;
    if (!divRef.current) return;
    if (!submitProblem) return;

    for (const question of submitProblem.questions || []) {
      for (const entry of question?.submitGradeEntries || []) {
        const tr = divRef.current.querySelector(
          `[id="${entry.trId}"]`,
        ) as HTMLTableRowElement | null;

        if (tr) {
          tr.style.setProperty('outline', `2px solid ${entry.isCorrect ? 'blue' : 'red'}`);
          tr.style.setProperty('outline-offset', `-4px`);
        }
      }
    }
  }, [submitProblem]);

  const isLoading = isAssignmentLoading || isSubmitLoading;

  if (isLoading) {
    return (
      <Card className="flex p-4">
        <div className="w-full flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <div className="text-gray-600">데이터를 불러오는 중...</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col p-4 overflow-x-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full mb-4">
        <div className="flex items-center gap-2 text-blue-800">
          <Info className="h-4 w-4" />
          <span className="text-sm font-medium">
            정답과 해설은 상단의 "정답&해설보기" 탭에서 확인하실 수 있습니다.
          </span>
        </div>
      </div>
      <AssignmentStep
        problemId={problemId}
        setProblemId={setProblemId}
        problems={assignment?.data?.problems ?? []}
      />

      <div className="w-[1062px] max-w-full mx-auto" ref={divRef}>
        <div className="flex flex-col gap-4">
          <div className="text-lg font-bold">문제 {(problemIndex ?? 0) + 1}</div>
          {problem && (
            <div>
              <div className="flex flex-col gap-4 items-center w-full">
                <p className="text-base font-bold text-left w-full">공통 영역</p>
                <div
                  className="border rounded-lg shadow w-full p-[10px] overflow-x-auto"
                  dangerouslySetInnerHTML={{
                    __html: problem.problemHTML,
                  }}
                />
              </div>
            </div>
          )}
          {problem?.questions.map((question, questionIndex) => {
            const submitQuestion = submitProblem?.questions.find(q => q.id === question.id);

            return (
              <div key={question.id} className="flex flex-col gap-4 border-b pb-4 last:border-b-0">
                <div className="text-lg font-bold">{questionIndex + 1}번 질문</div>

                <div className="flex flex-col gap-4 items-center w-full">
                  <p className="text-base font-bold flex text-left w-full">질문 영역</p>
                  <div className="border rounded-lg shadow p-[10px] w-full">
                    {question.questionHTML ? (
                      parseEditor(question.questionHTML)
                    ) : (
                      <div className="text-gray-500 text-center py-4">질문 영역이 없습니다.</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4 items-center w-full pointer-events-none">
                  <div className="flex justify-between w-full items-center">
                    <p className="text-base font-bold flex text-left">내 제출</p>
                    {!submitQuestion && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                        미제출
                      </span>
                    )}
                  </div>

                  {question.questionType !== 'SELECT' && (
                    <div className="border rounded-lg shadow p-[10px] w-full flex flex-col items-center">
                      <div className="w-full py-2 pointer-events-none">
                        {submitQuestion?.submitAnswerHTML ? (
                          parseEditor(submitQuestion.submitAnswerHTML)
                        ) : question.answerHTML ? (
                          parseEditor(question.answerHTML)
                        ) : (
                          <div className="text-gray-500 text-center py-4">
                            제출된 답안이 없습니다.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {question.questionType === 'SELECT' && (
                    <div
                      className="w-full flex flex-col gap-4 py-4 rounded"
                      id={question.id}
                      style={{
                        outline: submitQuestion
                          ? submitQuestion.submitGradeEntries?.some(entry => entry.isCorrect)
                            ? '3px solid blue'
                            : '3px solid red'
                          : '3px solid gray',
                        outlineOffset: '-2px',
                      }}
                    >
                      {(question?.choices || []).map((choice, index) => {
                        const isSelected = submitQuestion?.answer
                          ? submitQuestion.answer.selectedId === choice.id
                          : false;

                        return (
                          <React.Fragment key={index}>
                            <div className="flex gap-4 px-4">
                              <input
                                type="radio"
                                id={`${choice.id}.${index}`}
                                name={`question-${question.id}`}
                                checked={isSelected}
                                readOnly
                              />
                              <label htmlFor={`${choice.id}.${index}`}>{index + 1} 번 보기</label>
                            </div>
                            <div
                              className="border rounded-lg shadow p-[10px] w-full flex flex-col items-center"
                              key={choice.id}
                            >
                              <div
                                className="w-full py-2 answer"
                                dangerouslySetInnerHTML={{
                                  __html: choice.answerHTML,
                                }}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default MySubmit;
